import { styled } from '@mui/material';
import { default as MaterialChip, ChipProps as MaterialChipProps } from '@mui/material/Chip';

import { shouldForwardProps } from '../../utils/mui-styled.utils';

export interface ChipState {
	isSelected?: boolean;
	isGhost?: boolean;
	iconPosition?: 'start' | 'end';
	disabled?: boolean;
	size?: MaterialChipProps['size'];
	hasIcon: boolean;
	hasLabel: boolean;
}

export const StyledChip = styled(MaterialChip, {
	name: 'Chip',
	slot: 'Root',
	shouldForwardProp: shouldForwardProps([
		'isSelected',
		'isGhost',
		'iconPosition',
		'isClickableOnce',
		'hasIcon',
		'hasLabel',
	]),
})<ChipState>(
	({
		theme: {
			palette: {
				colorScheme: { colorVariables },
			},
		},
		isSelected,
		isGhost,
		iconPosition,
		disabled,
		hasIcon,
		hasLabel,
	}) => ({
		...(isGhost && {
			backgroundColor: 'transparent',
			color: colorVariables.colorCaption,
			boxShadow: `0px 0px 0px 1px ${colorVariables.colorBorder}`,
			'& > svg': {
				fill: colorVariables.colorBorder,
				color: colorVariables.colorBorder,
			},
			'&:hover': {
				backgroundColor: 'hsla(0, 0%, 100%, 0.4)',
				boxShadow: `0px 0px 0px 1px ${colorVariables.colorNote}`,
				transition: 'background-color 0.1s, box-shadow 0.1s',
			},
			'&:focus:not(:hover)': {
				backgroundColor: 'transparent',
				boxShadow: `0px 0px 0px 1px ${colorVariables.colorBorder}`,
			},
			'&:focus:not(:focus-visible), &:active:not(:focus-visible)': {
				backgroundColor: 'transparent',
				color: colorVariables.colorCaption,
				boxShadow: `0px 0px 0px 1px ${colorVariables.colorBorder}`,
			},
			'&:active:not(:focus-visible), &:active': {
				backgroundColor: 'hsla(0, 0%, 100%, 0.4)',
				filter: 'brightness(88%)',
				boxShadow: `0px 0px 0px 1px ${colorVariables.colorNote}`,
				transition: 'background-color 0.1s, box-shadow 0.1s',
			},
			'&:focus-visible': {
				backgroundColor: 'inherit',
				outline: `2px solid ${colorVariables.colorPrimary}`,
				outlineOffset: 2,
				transition: 'outline 0s',
			},
		}),
		...(isGhost && disabled && { boxShadow: 'none' }),
		...(!hasIcon &&
			hasLabel && {
				'& > span': {
					paddingLeft: 0,
				},
			}),
		...(iconPosition === 'end' && {
			flexDirection: 'row-reverse',
			'& > span': {
				paddingLeft: 0,
				paddingRight: 8,
			},
		}),
		...(hasIcon &&
			!hasLabel && {
				'&> svg': {
					marginInline: '0 !important',
					color: 'inherit',
					width: 24,
					height: 24,
				},
				'& > span': {
					display: 'none',
				},
			}),
		...(isSelected && {
			boxShadow: `0px 0px 0px 1px ${colorVariables.colorPrimary}`,
			'&:focus:not(:focus-visible), &:active:not(:focus-visible)': {
				boxShadow: `0px 0px 0px 1px ${colorVariables.colorPrimary}`,
			},
			'&:hover': {
				boxShadow: `0px 0px 0px 1px ${colorVariables.colorPrimary}`,
			},
			...(disabled && {
				'&:focus:not(:focus-visible), &:active:not(:focus-visible)': {
					backgroundColor: colorVariables.colorInactive,
					boxShadow: `0px 0px 0px 1px ${colorVariables.colorBorder}`,
				},
				boxShadow: `0px 0px 0px 1px ${colorVariables.colorBorder}`,
			}),
		}),

		...(disabled && {
			'& > svg': {
				color: colorVariables.colorBorder,
			},
			'& > svg:first-of-type': {
				fill: colorVariables.colorBorder,
			},
		}),
	}),
);
